import {
  CALLOUT_NODE,
  DEFAULT_CALLOUT_EMOJI,
  DEFAULT_CALLOUT_HUE,
} from "@repo/fika-blog/src/nodes/callout-node"
import {
  EMBED_NODE,
  hasRichEmbed,
  toEmbedData,
} from "@repo/fika-blog/src/nodes/embed/embed-node"
import { IMAGE_NODE } from "@repo/fika-blog/src/nodes/imageNode"
import type { Editor } from "@repo/fika-blog/src/tiptap/core"
import { NodeSelection } from "@repo/fika-blog/src/tiptap/pm/state"
import type { AccentHue } from "@repo/fika-design-system/color"
import type { UploadFolder } from "@repo/fika-shared/src/upload-folder.js"
import { throttle } from "@solid-primitives/scheduled"
import { assignInlineVars } from "@vanilla-extract/dynamic"
import {
  type Accessor,
  createEffect,
  createMemo,
  createSignal,
  Match,
  onCleanup,
  Switch,
} from "solid-js"
import { Icon } from "@/designSystem/components/icon"
import { useI18nContext } from "@/i18n/i18n-solid"
import {
  blockDotsClass,
  blockDotsTriggerClass,
  bmLeft,
  bmRight,
  bmTop,
} from "./block-node-menu.css"
import { CalloutMenu, type CalloutNodeMenuAttributes } from "./callout-menu"
import { CodeMenu } from "./code-menu"
import { DefaultMenu } from "./default-menu"
import { EmbedMenu } from "./embed-menu"
import { ImageMenu, type ImageNodeMenuAttributes } from "./image-menu"

type BlockNodeMenuProps = {
  editor?: Editor
  disableFloatingMenu?: boolean
  uploadFolder: Accessor<UploadFolder>
}
type BlockMenuState = {
  type: string
  from: number
  to: number
  top: number
  left: number
  right: number
}

export const BLOCK_NODE_MENU_WIDTH = 36

export function BlockNodeMenu(props: BlockNodeMenuProps) {
  const { LL } = useI18nContext()
  let dragPreviewWrapper: HTMLDivElement | undefined
  let dragging = false
  let editorDom: HTMLElement | null = null
  let suppressClickAfterDrag = false
  let inPointerDown = false
  let pendingOpen = false

  const [currentBlock, setCurrentBlock] = createSignal<BlockMenuState | null>(
    null,
  )
  const [menuOpen, setMenuOpen] = createSignal(false)

  const dotsStyle = createMemo(
    (prev: ReturnType<typeof assignInlineVars>) => {
      const block = currentBlock()
      if (block) {
        return assignInlineVars({
          [bmLeft]: `${block.left - BLOCK_NODE_MENU_WIDTH}px`,
          [bmRight]: `${block.right - BLOCK_NODE_MENU_WIDTH}px`,
          [bmTop]: `${block.top}px`,
        })
      }
      return prev
    },
    assignInlineVars({ [bmLeft]: "-36px", [bmRight]: "0px", [bmTop]: "0px" }),
  )

  // do not memo it since this uses dragging which is not a signal
  function isLocked() {
    return menuOpen() || dragging
  }

  function getBlockStateAtTarget(
    target: EventTarget | null,
  ): BlockMenuState | null {
    const editor = props.editor
    if (!editor || !(target instanceof Node)) return null
    if (props.disableFloatingMenu) return null

    let pos: number | null = null
    try {
      pos = editor.view.posAtDOM(target, 0)
    } catch {
      return null
    }
    if (pos === null) return null

    const clampedPos = Math.max(0, Math.min(pos, editor.state.doc.content.size))
    const $pos = editor.state.doc.resolve(clampedPos)

    let selectedDepth: number | null = null

    for (let depth = $pos.depth; depth >= 1; depth -= 1) {
      const nodeAtDepth = $pos.node(depth)
      if (!nodeAtDepth.isBlock) continue

      const parentDepth = depth - 1
      const parentNode = parentDepth >= 0 ? $pos.node(parentDepth) : null

      if (nodeAtDepth.type.name === "listItem") {
        selectedDepth = depth
        break
      }

      if (parentNode?.type.name === "blockquote") {
        selectedDepth = depth
        break
      }

      if (parentNode?.type.name === CALLOUT_NODE) {
        if ($pos.index(parentDepth) > 0) {
          selectedDepth = depth
          break
        }
        selectedDepth = parentDepth
        break
      }
    }

    let node
    let from: number
    let to: number

    if (selectedDepth !== null) {
      node = $pos.node(selectedDepth)
      from = $pos.before(selectedDepth)
      to = from + node.nodeSize
    } else if ($pos.depth === 0) {
      node = $pos.nodeAfter
      if (!node?.isBlock) return null
      from = clampedPos
      to = from + node.nodeSize
    } else {
      node = $pos.node(1)
      if (!node?.isBlock) return null
      from = $pos.before(1)
      to = from + node.nodeSize
    }

    if (node.type.name === "bulletList" || node.type.name === "orderedList") {
      return null
    }

    const domNode = editor.view.nodeDOM(from)
    if (!(domNode instanceof HTMLElement)) return null

    const editorRect = editor.view.dom.getBoundingClientRect()
    const nodeRect = domNode.getBoundingClientRect()
    const topPadding = 2

    return {
      type: node.type.name,
      from,
      to,
      top: nodeRect.top - editorRect.top + topPadding,
      left: nodeRect.left - editorRect.left,
      right: nodeRect.right - editorRect.left,
    }
  }

  function getBlockFromCoordinates(
    x: number,
    y: number,
  ): BlockMenuState | null {
    const editor = props.editor
    if (!editor) return null

    const probeX = x + 40
    const elements = editor.view.root.elementsFromPoint(probeX, y)
    for (const element of elements) {
      if (!(element instanceof HTMLElement)) continue
      if (!editor.view.dom.contains(element)) continue
      const state = getBlockStateAtTarget(element)
      if (!state) continue
      const blockDom = editor.view.nodeDOM(state.from)
      if (!(blockDom instanceof HTMLElement)) return state
      const blockRect = blockDom.getBoundingClientRect()
      if (
        // We leave some space to the left so we can hover around the block-menu area
        x >= blockRect.left - BLOCK_NODE_MENU_WIDTH &&
        x <= blockRect.right &&
        y >= blockRect.top &&
        y <= blockRect.bottom
      ) {
        return state
      }
    }
    return null
  }

  createEffect(() => {
    const editor = props.editor
    if (!editor || props.disableFloatingMenu) return

    editorDom = editor.view.dom

    const scheduleHoverUpdate = throttle((event: MouseEvent) => {
      if (isLocked()) return
      const block = getBlockFromCoordinates(event.clientX, event.clientY)
      setCurrentBlock(block)
    }, 32)

    function onMouseMove(event: MouseEvent) {
      scheduleHoverUpdate(event)
    }

    function onMouseLeave(event: MouseEvent) {
      scheduleHoverUpdate.clear()
      if (isLocked()) {
        return
      }
      const related = event.relatedTarget as HTMLElement | null
      const goingToDots = !!related?.closest?.("[data-block-dots]")
      if (goingToDots) return
      setCurrentBlock(null)
    }

    function onClick(event: MouseEvent) {
      if (isLocked()) return
      const block = getBlockStateAtTarget(event.target)
      setCurrentBlock(block)
    }

    function onKeyDown() {
      if (isLocked()) return
      setCurrentBlock(null)
    }

    editorDom.addEventListener("mousemove", onMouseMove)
    editorDom.addEventListener("mouseleave", onMouseLeave)
    editorDom.addEventListener("click", onClick)
    editorDom.addEventListener("keydown", onKeyDown)

    onCleanup(() => {
      scheduleHoverUpdate.clear()
      editorDom!.removeEventListener("mousemove", onMouseMove)
      editorDom!.removeEventListener("mouseleave", onMouseLeave)
      editorDom!.removeEventListener("click", onClick)
      editorDom!.removeEventListener("keydown", onKeyDown)
      editorDom = null
    })
  })

  const calloutMenuAttributes = createMemo<CalloutNodeMenuAttributes>(() => {
    const editor = props.editor
    const state = currentBlock()

    if (!editor || !state || state.type !== CALLOUT_NODE) {
      return { hue: DEFAULT_CALLOUT_HUE, emoji: DEFAULT_CALLOUT_EMOJI }
    }

    const node = editor.state.doc.nodeAt(state.from)
    if (!node || node.type.name !== CALLOUT_NODE) {
      return { hue: DEFAULT_CALLOUT_HUE, emoji: DEFAULT_CALLOUT_EMOJI }
    }

    return {
      hue: (node.attrs.hue ?? DEFAULT_CALLOUT_HUE) as AccentHue,
      emoji: (node.attrs.emoji ?? DEFAULT_CALLOUT_EMOJI) as string,
    }
  })

  const imageMenuAttributes = createMemo<ImageNodeMenuAttributes>(() => {
    const editor = props.editor
    const state = currentBlock()

    if (!editor || !state || state.type !== "image") {
      return { title: "", size: "medium" }
    }

    const node = editor.state.doc.nodeAt(state.from)
    if (!node || node.type.name !== IMAGE_NODE) {
      return { title: "", size: "medium" }
    }

    return {
      title: (node.attrs.title ?? "") as string,
      size: (node.attrs.size ?? "medium") as string,
    }
  })

  const codeLanguage = createMemo(() => {
    const editor = props.editor
    const state = currentBlock()

    if (!editor || !state || state.type !== "codeBlock") return "text"

    const node = editor.state.doc.nodeAt(state.from)
    if (!node || node.type.name !== "codeBlock") return "text"

    return (node.attrs.language ?? "text") as string
  })

  const embedMenuAttributes = createMemo(() => {
    const editor = props.editor
    const state = currentBlock()

    if (!editor || !state || state.type !== EMBED_NODE) {
      return { displayMode: "interactive", hasRichEmbed: false }
    }

    const node = editor.state.doc.nodeAt(state.from)
    if (!node || node.type.name !== EMBED_NODE) {
      return { displayMode: "interactive", hasRichEmbed: false }
    }

    const data = toEmbedData(node.attrs)
    return {
      displayMode: (data.displayMode ?? "interactive") as string,
      hasRichEmbed: hasRichEmbed(data),
    }
  })

  function focusMenuBlock() {
    const state = currentBlock()
    const editor = props.editor
    if (!state || !editor) return false

    if (state.type === "image") {
      editor.chain().focus().setNodeSelection(state.from).run()
      return true
    }

    const textPos = Math.min(state.from + 1, state.to - 1)
    editor.chain().focus().setTextSelection(textPos).run()
    return true
  }

  function runOnMenuBlock(
    action: (editor: Editor) => void,
    options: { forType?: string } = {},
  ) {
    const state = currentBlock()
    const editor = props.editor
    if (!state || !editor) return
    if (options.forType && state.type !== options.forType) return

    focusMenuBlock()
    action(editor)
  }

  function updateCalloutHue(hue: AccentHue) {
    runOnMenuBlock(
      editor => {
        editor
          .chain()
          .focus()
          .updateAttributes(CALLOUT_NODE, {
            hue,
          })
          .run()
      },
      { forType: CALLOUT_NODE },
    )
  }

  function updateImageAttributes(attrs: Partial<ImageNodeMenuAttributes>) {
    const state = currentBlock()
    const editor = props.editor
    if (!state || !editor || state.type !== "image") return

    const node = editor.state.doc.nodeAt(state.from)
    if (!node || node.type.name !== IMAGE_NODE) return

    editor.view.dispatch(
      editor.state.tr.setNodeMarkup(state.from, undefined, {
        ...node.attrs,
        ...attrs,
      }),
    )
  }

  function updateCodeLanguage(language: string) {
    runOnMenuBlock(
      editor => {
        editor.chain().focus().updateAttributes("codeBlock", { language }).run()
      },
      { forType: "codeBlock" },
    )
  }

  function updateEmbedDisplayMode(mode: string) {
    const state = currentBlock()
    const editor = props.editor
    if (!state || !editor || state.type !== EMBED_NODE) return

    const node = editor.state.doc.nodeAt(state.from)
    if (!node || node.type.name !== EMBED_NODE) return

    editor.view.dispatch(
      editor.state.tr.setNodeMarkup(state.from, undefined, {
        ...node.attrs,
        displayMode: mode,
      }),
    )
  }

  function transformBlockTo(value: string) {
    const editor = props.editor
    if (!editor || !focusMenuBlock()) return

    const state = currentBlock()
    const currentNodeType = state?.type
    const chain = editor.chain().focus()

    if (currentNodeType === "blockquote" && value !== "blockquote") {
      chain.unsetBlockquote()
    }
    if (currentNodeType === CALLOUT_NODE && value !== CALLOUT_NODE) {
      chain.unsetCallout()
    }

    switch (value) {
      case "heading-1":
        chain.setNode("heading", { level: 1 }).run()
        return
      case "heading-2":
        chain.setNode("heading", { level: 2 }).run()
        return
      case "heading-3":
        chain.setNode("heading", { level: 3 }).run()
        return
      case "blockquote":
        chain.setBlockquote().run()
        return
      case CALLOUT_NODE:
        chain
          .setCallout()
          .updateAttributes(CALLOUT_NODE, {
            hue: calloutMenuAttributes().hue,
            emoji: calloutMenuAttributes().emoji,
          })
          .run()
        return
      case "paragraph":
        chain.setParagraph().run()
        return
      case "codeBlock":
        chain.setNode("codeBlock").run()
        return
    }
  }

  function deleteBlock() {
    const block = currentBlock()
    const editor = props.editor
    if (!block || !editor) return

    editor.chain().focus().deleteRange({ from: block.from, to: block.to }).run()
    setMenuOpen(false)
    setCurrentBlock(null)
  }

  function cleanupDragPreview() {
    if (!dragPreviewWrapper) return
    dragPreviewWrapper.remove()
    dragPreviewWrapper = undefined
  }

  function onHandleTriggerPointerDown() {
    inPointerDown = true
  }

  function onHandleTriggerClick(event: MouseEvent) {
    const wasPendingOpen = pendingOpen
    inPointerDown = false
    pendingOpen = false

    if (dragging || suppressClickAfterDrag) {
      event.preventDefault()
      event.stopPropagation()
      suppressClickAfterDrag = false
      return
    }

    if (wasPendingOpen) setMenuOpen(true)
  }

  function onHandleDragStart(event: DragEvent) {
    const editor = props.editor
    const block = currentBlock()
    const dataTransfer = event.dataTransfer

    if (!editor || !block || !dataTransfer) {
      event.preventDefault()
      return
    }

    dragging = true
    suppressClickAfterDrag = true
    inPointerDown = false
    pendingOpen = false
    setMenuOpen(false)

    const nodeSelection = NodeSelection.create(editor.state.doc, block.from)
    const tr = editor.state.tr.setSelection(nodeSelection)
    editor.view.dispatch(tr)
    editor.view.focus()

    const selectionSlice = nodeSelection.content()
    const clipboardData = editor.view.serializeForClipboard(selectionSlice)
    try {
      dataTransfer.clearData()
    } catch {
      // no-op
    }
    dataTransfer.setData("text/html", clipboardData.dom.innerHTML)
    dataTransfer.setData("text/plain", clipboardData.text)
    dataTransfer.effectAllowed = "copyMove"

    const dragView = editor.view as typeof editor.view & {
      dragging?: {
        slice: typeof clipboardData.slice
        move: boolean
        node?: NodeSelection
      } | null
    }
    dragView.dragging = {
      slice: clipboardData.slice,
      move: true,
      node: nodeSelection,
    }

    const sourceNode = editor.view.nodeDOM(block.from)
    if (sourceNode instanceof HTMLElement) {
      const wrapper = document.createElement("div")
      const clone = sourceNode.cloneNode(true)
      if (clone instanceof HTMLElement) {
        wrapper.style.position = "absolute"
        wrapper.style.top = "-10000px"
        wrapper.style.left = "-10000px"
        clone.style.opacity = "0.65"
        clone.style.pointerEvents = "none"
        wrapper.appendChild(clone)
        document.body.appendChild(wrapper)
        dragPreviewWrapper = wrapper
        dataTransfer.setDragImage(wrapper, 0, 0)
      }
    }

    document.addEventListener("drop", cleanupDragPreview, { once: true })
  }

  function onHandleDragEnd() {
    dragging = false
    cleanupDragPreview()
    setCurrentBlock(null)

    window.setTimeout(() => {
      suppressClickAfterDrag = false
    }, 0)

    const editor = props.editor
    if (!editor) return
    const dragView = editor.view as typeof editor.view & {
      dragging?: unknown
    }
    dragView.dragging = null
  }

  function onDotsMouseLeave(event: MouseEvent) {
    if (isLocked()) {
      return
    }
    const related = event.relatedTarget as HTMLElement | null
    const goingToEditor = !!(related && editorDom?.contains(related))
    if (goingToEditor) return
    setCurrentBlock(null)
  }

  function handleMenuOpenChange(isOpen: boolean) {
    if (isOpen && inPointerDown) {
      pendingOpen = true
      return
    }
    pendingOpen = false
    setMenuOpen(isOpen)
  }

  return (
    <div
      data-block-dots
      class={blockDotsClass({ visible: !!currentBlock() })}
      style={dotsStyle()}
      onMouseLeave={onDotsMouseLeave}
    >
      <Switch>
        <Match when={currentBlock()?.type === CALLOUT_NODE}>
          <CalloutMenu
            isOpen={menuOpen()}
            onOpenChange={handleMenuOpenChange}
            triggerClass={blockDotsTriggerClass}
            triggerLabel={LL().editor.more()}
            onTriggerPointerDown={onHandleTriggerPointerDown}
            onTriggerClick={onHandleTriggerClick}
            onTriggerKeyDown={(event: KeyboardEvent) => {
              if (event.key === "Escape") setMenuOpen(false)
            }}
            onTriggerDragStart={onHandleDragStart}
            onTriggerDragEnd={onHandleDragEnd}
            onUpdateHue={updateCalloutHue}
            onDelete={deleteBlock}
          >
            <Icon name="grip-vertical" size="18" />
          </CalloutMenu>
        </Match>
        <Match when={currentBlock()?.type === "image"}>
          <ImageMenu
            isOpen={menuOpen()}
            onOpenChange={handleMenuOpenChange}
            triggerClass={blockDotsTriggerClass}
            triggerLabel={LL().editor.more()}
            onTriggerPointerDown={onHandleTriggerPointerDown}
            onTriggerClick={onHandleTriggerClick}
            onTriggerKeyDown={e => {
              if (e.key === "Escape") setMenuOpen(false)
            }}
            onTriggerDragStart={onHandleDragStart}
            onTriggerDragEnd={onHandleDragEnd}
            attributes={imageMenuAttributes()}
            onUpdateImageAttributes={updateImageAttributes}
            onDelete={deleteBlock}
          >
            <Icon name="grip-vertical" size="18" />
          </ImageMenu>
        </Match>
        <Match when={currentBlock()?.type === "codeBlock"}>
          <CodeMenu
            isOpen={menuOpen()}
            onOpenChange={handleMenuOpenChange}
            triggerClass={blockDotsTriggerClass}
            triggerLabel={LL().editor.more()}
            onTriggerPointerDown={onHandleTriggerPointerDown}
            onTriggerClick={onHandleTriggerClick}
            onTriggerKeyDown={e => {
              if (e.key === "Escape") setMenuOpen(false)
            }}
            onTriggerDragStart={onHandleDragStart}
            onTriggerDragEnd={onHandleDragEnd}
            language={codeLanguage()}
            onUpdateLanguage={updateCodeLanguage}
            onDelete={deleteBlock}
          >
            <Icon name="grip-vertical" size="18" />
          </CodeMenu>
        </Match>
        <Match when={currentBlock()?.type === EMBED_NODE}>
          <EmbedMenu
            isOpen={menuOpen()}
            onOpenChange={handleMenuOpenChange}
            triggerClass={blockDotsTriggerClass}
            triggerLabel={LL().editor.more()}
            onTriggerPointerDown={onHandleTriggerPointerDown}
            onTriggerClick={onHandleTriggerClick}
            onTriggerKeyDown={e => {
              if (e.key === "Escape") setMenuOpen(false)
            }}
            onTriggerDragStart={onHandleDragStart}
            onTriggerDragEnd={onHandleDragEnd}
            displayMode={embedMenuAttributes().displayMode}
            hasRichEmbed={embedMenuAttributes().hasRichEmbed}
            onUpdateDisplayMode={updateEmbedDisplayMode}
            onDelete={deleteBlock}
          >
            <Icon name="grip-vertical" size="18" />
          </EmbedMenu>
        </Match>
        <Match when={!!currentBlock()}>
          <DefaultMenu
            isOpen={menuOpen()}
            onOpenChange={handleMenuOpenChange}
            triggerClass={blockDotsTriggerClass}
            triggerLabel={LL().editor.more()}
            onTriggerPointerDown={onHandleTriggerPointerDown}
            onTriggerClick={onHandleTriggerClick}
            onTriggerKeyDown={e => {
              if (e.key === "Escape") setMenuOpen(false)
            }}
            onTriggerDragStart={onHandleDragStart}
            onTriggerDragEnd={onHandleDragEnd}
            onTransform={transformBlockTo}
            onDelete={deleteBlock}
          >
            <Icon name="grip-vertical" size="18" />
          </DefaultMenu>
        </Match>
      </Switch>
    </div>
  )
}
